import { useSelector } from 'react-redux';

import { DataRemaining, DaysLeft, UsageBar } from '@app/components/UsageSummary';
import { selectUser } from '@app/redux/auth';
import { selectConfig } from '@app/redux/config';
import { selectPlanInfo, shouldMockTierDataForUser } from '@app/redux/plan';

import { Locale } from '@mfe/shared/schema-types';

import { useShowUnleashedUsage } from '../hooks';
import { UnleashedUsageSummary } from '../unleashed-usage-summary';
import { TotalDataUsed } from './total-data-used';
import { UnlimitedContent } from './unlimited-content';
import { UsageSummaryCard } from './usage-summary-card';

import styles from './styles.module.scss';

type Props = {
  isLoading: boolean;
  handleChangePlan: () => void;
  handlePlanDetailsClick: () => void;
  handleNavigateToPlanPage: () => void;
  userLocale: Locale;
};

export const LocalizedUsageSummary = ({
  isLoading,
  handleChangePlan,
  handlePlanDetailsClick,
  handleNavigateToPlanPage,
  userLocale,
}: Props) => {
  const commonProps = {
    isLoading,
    handlePlanDetailsClick,
    handleNavigateToPlanPage,
    id: 'usageSummary',
  };

  const { env, getMockTier } = useSelector(selectConfig);
  const { user } = useSelector(selectUser);

  const {
    data: {
      characteristics: { isUnleashed, hasUnlimitedUsageMeter },
    },
  } = useSelector(selectPlanInfo);

  const shouldShowUnleashedUsage = useShowUnleashedUsage();

  const showUnleashedUsageSummary = shouldShowUnleashedUsage || shouldMockTierDataForUser(user, env, getMockTier);

  switch (userLocale) {
    case Locale.EnUs:
      return showUnleashedUsageSummary ? (
        <UnleashedUsageSummary />
      ) : (
        <UsageSummaryCard handleChangePlan={handleChangePlan} {...commonProps} showFooter={!isUnleashed}>
          {hasUnlimitedUsageMeter ? <UnlimitedContent /> : <UsageData />}
        </UsageSummaryCard>
      );

    case Locale.PtBr:
      return (
        <UsageSummaryCard {...commonProps}>
          <UsageData />
        </UsageSummaryCard>
      );

    case Locale.ItIt:
      return (
        <UsageSummaryCard showFooter={false} {...commonProps}>
          <TotalDataUsed />
          <DaysLeft />
        </UsageSummaryCard>
      );

    default:
      return null;
  }
};

const UsageData = () => (
  <>
    <div className={styles['remaining']}>
      <DataRemaining />
      <DaysLeft />
    </div>
    <UsageBar />
  </>
);
